import { isEqual } from 'lodash'

import { Services } from '@sigma-frontend-libs/commons'
import { EVENTSHISTORY_ASSOCIATED_SERVICES, EVENTSHISTORY_TYPES } from '@sigma-legacy-libs/essentials/lib/constants'
import { isUUID } from '@sigmasms/commons'

import { getSocket, globalErrorHandler, projectName, request } from '@/utils'

import { $order, calcChanges } from '@/components/services/eventsHistory/utils'

import render from './render'

export default {
  name: 'Tab',

  props: {
    entityId: {
      type: String,
      required: true,
      validator: isUUID
    }
  },

  /**
   * Локальное состояние вкладки истории событий.
   *
   * Предел постраничного вывода читается из localStorage. Ошибка разбора
   * испорченного значения подавляется: в этом случае используется значение
   * по умолчанию.
   *
   * @returns {object} Локальное состояние вкладки.
   */
  data() {
    let limitFromLocalStorage = 25
    try {
      limitFromLocalStorage = JSON.parse(window.localStorage.getItem(`${projectName}:pagination:limit:${Services.eventsHistory}`))
    } catch (error) {
    }

    return {
      items: [],

      pagination: {
        limit: limitFromLocalStorage || 10,
        offset: 0,
        total: 0
      },

      loading: false,
      showDialog: false,

      currentTab: 'oldValues',

      compareData: {
        oldValues: {},
        newValues: {}
      },

      compareItemType: undefined,
      compareService: undefined,

      compareId: ''
    }
  },

  computed: {
    types() {
      return Object.keys(this.compareData).sort((a, b) => {
        return b.localeCompare(a)
      })
    },

    dialogTitle() {
      switch (this.compareItemType) {
        case EVENTSHISTORY_TYPES.created: return this.getTranslate(`${Services.eventsHistory}.labels.created`)
        case EVENTSHISTORY_TYPES.imported: return this.getTranslate(`${Services.eventsHistory}.labels.imported`)
        default: return this.getTranslate(`${Services.eventsHistory}.title`)
      }
    }
  },

  watch: {
    'pagination.offset': {
      handler() {
        this.getItems()
      },
      deep: true
    }
  },

  mounted() {
    this.getItems()

    const socket = getSocket()
    socket?.on(`${Services.eventsHistory}.created`, data => {
      if (data.entityId && data.entityId === this.entityId) {
        if (this.pagination.offset === 0) {
          this.items.unshift(data)
          if (this.items.length > this.pagination.limit) {
            this.items.splice(this.items.length - 1, 1)
          }
        }
        this.pagination.total++
      }
    })
  },

  methods: {
    async getItems() {
      try {
        this.loading = true

        const data = await request.find(Services.eventsHistory, undefined, {
          entityId: this.entityId,
          $offset: this.pagination.offset,
          $limit: this.pagination.limit,
          $order
        })

        if (data) {
          this.items = data.data
          this.pagination.total = data.total
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading = false
      }
    },

    async getNextItem(item, offset) {
      try {
        const query = {
          entityId: this.entityId,
          service: EVENTSHISTORY_ASSOCIATED_SERVICES[item.service],
          $offset: offset,
          $order
        }

        const data = await request.find(Services.eventsHistory, undefined, query)

        if (Array.isArray(data.data) && data.data.length) {
          const index = data.data.findIndex(v => {
            return v.data.id === item.data.id
          })
          if (index !== -1) {
            return data.data[index]
          }
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    async compareItems(item, index) {
      let nextItem
      this.compareId = item.data.id
      this.compareService = item.service
      this.compareItemType = item.type
      const comparedItem = this.items[index]
      switch (comparedItem.type) {
        case EVENTSHISTORY_TYPES.created:
        case EVENTSHISTORY_TYPES.imported: {
          nextItem = { data: {} }
          this.compareData = calcChanges.call(this, item, nextItem)
          this.currentTab = 'newValues'
          this.showDialog = true
          break
        }
        default: {
          switch (comparedItem.service) {
            case EVENTSHISTORY_ASSOCIATED_SERVICES.permissionsUsers:
            case EVENTSHISTORY_ASSOCIATED_SERVICES.settingsUserUsers: {
              const compareValues = {
                old: {},
                new: {}
              }
              for (const type of Object.keys(compareValues)) {
                compareValues[type] = { data: {} }
                const { value } = comparedItem.data[type]
                for (const field of Object.keys(comparedItem.data[type])) {
                  if (field !== 'value') {
                    compareValues[type].data[comparedItem.data[type][field]] = value
                  }
                }
              }
              if (isEqual(compareValues.new.data, compareValues.old.data)) {
                this.compareData.oldValues = compareValues.old.data
                this.compareData.newValues = compareValues.new.data
              } else {
                this.compareData = calcChanges.call(this, compareValues.new, compareValues.old)
              }
              this.showDialog = true

              break
            }

            default: {
              if (this.items[index + 1]) {
                nextItem = this.items.find((v, i) => {
                  return i > index
                  && v.service === comparedItem.service
                  && comparedItem.type === EVENTSHISTORY_TYPES.updated
                  && v.data.id === comparedItem.data.id
                })
              }
              if (!nextItem) {
                const response = await this.getNextItem(item, index + 1)
                if (response) {
                  nextItem = response
                }
              }
              if (nextItem && !isEqual(item.data, nextItem.data)) {
                this.compareData = calcChanges.call(this, item, nextItem)
                this.showDialog = true
              }
              break
            }
          }
          break
        }
      }
    },

    getLabel(value) {
      const label = []
      switch (this.compareService) {
        case EVENTSHISTORY_ASSOCIATED_SERVICES.permissionsUsers: {
          const [ , service, type ] = value.split('.')
          label.push(this.getTranslate(`${Services.eventsHistory}.labels.${this.compareService}`))
          label.push([
            this.getTranslate(`${Services.permissions}.labels.${type}`),
            this.getTranslate(`${Services.permissions}.services.${service}`).toLowerCase()
          ].join(' '))

          break
        }

        default: {
          const firstLowerCase = value[0].toLowerCase() + value.slice(1)
          label.push(this.getTranslate(`${Services.eventsHistory}.updatedValues.${firstLowerCase}`))

          break
        }
      }

      return label.join(': ')
    }
  },

  render
}
